// src/pages/ServicesCuratorial.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { Users, Euro } from "lucide-react";

const GOALS = [
  { value: "Structurer ma collection (thématiques, titres, mots-clés)", key: "structure" },
  { value: "Préparer une présentation/accrochage", key: "presentation" },
  { value: "Créer un récit (narratif, éditorial)", key: "narrative" },
  { value: "Audit curatorial complet", key: "audit" },
  { value: "Autre (je précise ci-dessous)", key: "other" },
];

export default function ServicesCuratorial() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, profile } = useAuth() || {};

  const preselectArtworkId = location?.state?.preselectArtworkId ?? null;

  const [email, setEmail] = useState("");
  const [goals, setGoals] = useState(GOALS[0].value);
  const [details, setDetails] = useState(
    preselectArtworkId
      ? t("servicesCuratorialPage.details.startingPoint", { id: preselectArtworkId })
      : ""
  );
  const [preferredContact, setPreferredContact] = useState("email");
  const [expertChoice, setExpertChoice] = useState("artkiv_selects");
  const [selectedExpertId, setSelectedExpertId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [artworkLabel, setArtworkLabel] = useState("");
  const [experts, setExperts] = useState([]);
  const [loadingExperts, setLoadingExperts] = useState(false);

  const scope = useMemo(
    () => (preselectArtworkId ? "artwork" : "collection"),
    [preselectArtworkId]
  );

  useEffect(() => {
    if (!preselectArtworkId) return;
    supabase
      .from("artworks")
      .select("title, artist")
      .eq("id", preselectArtworkId)
      .single()
      .then(({ data }) => {
        if (data) {
          const label = [data.title, data.artist].filter(Boolean).join(" — ");
          setArtworkLabel(label || preselectArtworkId);
        }
      });
  }, [preselectArtworkId]);

  useEffect(() => {
    const auto = profile?.email || user?.email || profile?.contact_email || "";
    setEmail(auto);
  }, [user?.email, profile?.email, profile?.contact_email]);

  useEffect(() => {
    if (expertChoice === "i_choose") fetchExperts();
  }, [expertChoice]);

  useEffect(() => {
    if (expertChoice !== "i_choose") setSelectedExpertId(null);
  }, [expertChoice]);

  async function fetchExperts() {
    setLoadingExperts(true);
    try {
      const { data, error } = await supabase
        .from("experts")
        .select("id, name, specialty, bio, image_url")
        .eq("is_public", true)
        .is("directory_opt_out_at", null)
        .not("directory_opt_in_at", "is", null)
        .order("name", { ascending: true });

      if (error) throw error;
      setExperts(data || []);
    } catch (err) {
      console.error("[ServicesCuratorial] experts:", err);
    } finally {
      setLoadingExperts(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg("");

    try {
      const { error } = await supabase.from("partner_requests").insert([
        {
          client_id: user?.id ?? null,
          type: "curatorial",
          status: "pending",
          notes: details,
          payload: {
            goals,
            details,
            preselectArtworkId,
            scope,
            preferredContact,
            expertChoice,
            selectedExpertId: expertChoice === "i_choose" ? selectedExpertId : null,
          },
        },
      ]);
      if (error) throw error;
      setDone(true);
    } catch (err) {
      console.error(err);
      setErrorMsg(t("servicesCuratorialPage.error.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8" style={{ fontFamily: "Georgia, serif", backgroundColor: "#fafaf9" }}>
        <button onClick={() => navigate(-1)} className="text-sm mb-4" style={{ color: "#1a1a1a", textDecoration: "underline" }}>
          {t("servicesCuratorialPage.back")}
        </button>
        <h1 className="text-3xl font-semibold mb-3" style={{ color: "#1a1a1a" }}>
          {t("servicesCuratorialPage.success.title")}
        </h1>
        <p style={{ color: "#4a4a4a", lineHeight: "1.6" }}>
          <Trans
            i18nKey={scope === "artwork" ? "servicesCuratorialPage.success.artwork" : "servicesCuratorialPage.success.collection"}
            components={{ strong: <strong /> }}
          />
        </p>
        <button className="mt-6 px-4 py-2 rounded-md" style={{ backgroundColor: "#1a1a1a", color: "white" }} onClick={() => navigate("/my-collection")}>
          {t("servicesCuratorialPage.success.backCollection")}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" style={{ fontFamily: "Georgia, serif", backgroundColor: "#fafaf9" }}>
      <button onClick={() => navigate(-1)} className="text-sm mb-4" style={{ color: "#1a1a1a", textDecoration: "underline" }}>
        {t("servicesCuratorialPage.back")}
      </button>

      <h1 className="text-3xl font-semibold mb-2" style={{ color: "#1a1a1a" }}>
        {t("servicesCuratorialPage.title")}
      </h1>
      <p className="mb-6 max-w-2xl" style={{ color: "#4a4a4a", lineHeight: "1.6" }}>
        <Trans i18nKey="servicesCuratorialPage.intro" components={{ strong: <strong /> }} />
      </p>

      <div className="rounded-xl p-5 mb-6 flex items-start gap-3" style={{ border: "1px solid #e5e5e5", backgroundColor: "#fff7ed" }}>
        <Euro className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#c9a961" }} />
        <div>
          <h3 className="font-semibold mb-1" style={{ color: "#1a1a1a" }}>
            {t("servicesCuratorialPage.pricing.title")}
          </h3>
          <p className="text-sm" style={{ color: "#4a4a4a", lineHeight: "1.5" }}>
            <Trans i18nKey="servicesCuratorialPage.pricing.description" components={{ strong: <strong /> }} />
          </p>
        </div>
      </div>

      <div className="rounded-2xl p-6" style={{ border: "1px solid #e5e5e5", backgroundColor: "white" }}>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                {t("servicesCuratorialPage.scope.label")}
              </label>
              <div className="text-sm">
                <span className="px-3 py-1 rounded" style={{ backgroundColor: scope === "collection" ? "#f0f0f0" : "#fff7ed", border: "1px solid #d4d4d4", color: "#1a1a1a" }}>
                  {scope === "collection" ? t("servicesCuratorialPage.scope.collection") : artworkLabel || preselectArtworkId}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                {t("servicesCuratorialPage.goals.label")}
              </label>
              <select value={goals} onChange={(e) => setGoals(e.target.value)} className="w-full border rounded-md px-3 py-2" style={{ backgroundColor: "white", borderColor: "#d4d4d4" }}>
                {GOALS.map((goal) => (
                  <option key={goal.key} value={goal.value}>{t(`servicesCuratorialPage.goals.options.${goal.key}`)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                {t("servicesCuratorialPage.details.label")}
              </label>
              <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={5} placeholder={t("servicesCuratorialPage.details.placeholder")} className="w-full border rounded-md px-3 py-2" style={{ backgroundColor: "white", borderColor: "#d4d4d4" }} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#1a1a1a" }}>
                <Users className="w-4 h-4 inline mr-1" />
                {t("servicesCuratorialPage.expert.label")}
              </label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="expertChoice" value="artkiv_selects" checked={expertChoice === "artkiv_selects"} onChange={(e) => setExpertChoice(e.target.value)} className="mt-1" />
                  <div>
                    <div className="font-medium text-sm">{t("servicesCuratorialPage.expert.auto.title")}</div>
                    <div className="text-xs text-gray-500">{t("servicesCuratorialPage.expert.auto.description")}</div>
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="expertChoice" value="i_choose" checked={expertChoice === "i_choose"} onChange={(e) => setExpertChoice(e.target.value)} className="mt-1" />
                  <div>
                    <div className="font-medium text-sm">{t("servicesCuratorialPage.expert.choose.title")}</div>
                    <div className="text-xs text-gray-500">{t("servicesCuratorialPage.expert.choose.description")}</div>
                  </div>
                </label>
              </div>

              {expertChoice === "i_choose" && (
                <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                  {loadingExperts ? (
                    <div className="text-sm text-gray-500">{t("servicesCuratorialPage.expert.loading")}</div>
                  ) : experts.length === 0 ? (
                    <div className="text-sm text-gray-500">{t("servicesCuratorialPage.expert.empty")}</div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {experts.map((expert) => (
                        <label key={expert.id} className="flex items-start gap-3 p-2 rounded cursor-pointer hover:bg-white transition">
                          <input type="radio" name="selectedExpert" value={expert.id} checked={selectedExpertId === expert.id} onChange={() => setSelectedExpertId(expert.id)} className="mt-1" />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{expert.name || t("servicesCuratorialPage.expert.generic")}</div>
                            <div className="text-xs text-gray-600">{expert.specialty}</div>
                            {expert.bio && <div className="text-xs text-gray-500 mt-1 line-clamp-2">{expert.bio}</div>}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  <button type="button" onClick={() => navigate("/experts")} className="mt-2 text-xs underline" style={{ color: "#c9a961" }}>
                    {t("servicesCuratorialPage.expert.directory")}
                  </button>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                  {t("servicesCuratorialPage.contact.email")}
                </label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full border rounded-md px-3 py-2" style={{ backgroundColor: "white", borderColor: "#d4d4d4" }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                  {t("servicesCuratorialPage.contact.preference")}
                </label>
                <select value={preferredContact} onChange={(e) => setPreferredContact(e.target.value)} className="w-full border rounded-md px-3 py-2" style={{ backgroundColor: "white", borderColor: "#d4d4d4" }}>
                  <option value="email">{t("servicesCuratorialPage.contact.methods.email")}</option>
                  <option value="phone">{t("servicesCuratorialPage.contact.methods.phone")}</option>
                  <option value="visio">{t("servicesCuratorialPage.contact.methods.video")}</option>
                </select>
              </div>
            </div>

            {errorMsg && <div className="text-sm" style={{ color: "#dc2626" }}>{errorMsg}</div>}

            <div className="pt-2">
              <button type="submit" disabled={isSubmitting || (expertChoice === "i_choose" && !selectedExpertId)} className="px-4 py-2 rounded-md disabled:opacity-60" style={{ backgroundColor: "#1a1a1a", color: "white" }}>
                {isSubmitting ? t("servicesCuratorialPage.submit.sending") : t("servicesCuratorialPage.submit.send")}
              </button>
            </div>
          </div>
        </form>
      </div>

      <p className="text-xs mt-4" style={{ color: "#6b7280" }}>
        <Trans i18nKey="servicesCuratorialPage.note" components={{ strong: <strong /> }} />
      </p>
    </div>
  );
}
